import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

function renderMarkdown(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let idx = 0;

  while (idx < lines.length) {
    const line = lines[idx];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={idx} className="text-xl font-bold text-foreground-950 mt-10 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
      idx++;
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={idx} className="text-lg font-semibold text-foreground-900 mt-8 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
      idx++;
      continue;
    }

    if (line.startsWith('|')) {
      const tableLines: string[] = [];
      while (idx < lines.length && lines[idx].startsWith('|')) {
        tableLines.push(lines[idx]);
        idx++;
      }
      const headers = tableLines[0].split('|').filter(Boolean).map((h) => h.trim());
      const body = tableLines.slice(2).map((row) =>
        row.split('|').filter(Boolean).map((c) => c.trim())
      );

      elements.push(
        <div key={idx} className="overflow-x-auto my-6 rounded-lg border border-background-200/60">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary-100">
                {headers.map((h, hi) => (
                  <th key={hi} className="px-4 py-3 text-left whitespace-nowrap font-semibold text-foreground-800">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 whitespace-nowrap text-foreground-700">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (line.startsWith('- ') || line.match(/^\d+\.\s/)) {
      const listItems: string[] = [];
      while (idx < lines.length && (lines[idx].startsWith('- ') || lines[idx].match(/^\d+\.\s/))) {
        listItems.push(lines[idx].replace(/^[-]\s|^\d+\.\s/, '').trim());
        idx++;
      }
      const isOrdered = lines[idx - listItems.length]?.match(/^\d+\.\s/);
      const ListTag = isOrdered ? 'ol' : 'ul';
      elements.push(
        <ListTag key={idx} className={`${isOrdered ? 'list-decimal' : 'list-disc'} pl-6 my-4 space-y-2`}>
          {listItems.map((item, li) => (
            <li key={li} className="text-foreground-700 leading-relaxed text-sm">{item}</li>
          ))}
        </ListTag>
      );
      continue;
    }

    if (line.trim() === '') {
      idx++;
      continue;
    }

    elements.push(
      <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-sm">
        {line}
      </p>
    );
    idx++;
  }

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 亚洲五码对照指南`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter(
    (a) => article.relatedIds.includes(a.id)
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.createdAt,
    author: {
      '@type': 'Organization',
      name: '亚洲五码对照指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '亚洲五码对照指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://docbihd.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: article.category, path: `/category/${article.categorySlug}` },
            { name: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
              >
                {article.category}
              </Link>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-4 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>发布日期：{article.createdAt}</span>
                <span>阅读时间：{article.readTime}</span>
                <div className="flex flex-wrap gap-1">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-foreground-400">#{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-content">
              {renderMarkdown(article.content)}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100/70">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}